import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Input, InputGroup, InputGroupText,
    Button, Badge,
    Modal, ModalHeader, ModalBody,
    ListGroup, ListGroupItem,
} from "reactstrap";
import {
    SectionCard,
    ModuleShortcutCard,
    ModuleLandingGrid,
    EmptyState,
} from "../../Components/Common/DashboardKit";
import MenuCard from "../../Components/Common/MenuCard";
// Round 28 — drag-and-drop dashboard grid. Requires:  npm install react-grid-layout
// Hotfix: v1 of the library exposes WidthProvider(Responsive); v2 removed the
// HOC and ships a width-aware ResponsiveGridLayout directly. Import the whole
// module and adapt at runtime so either installed version works.
import * as RGLPkg from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import AnnouncementsBoard from "../../Components/Common/AnnouncementsBoard";
import {
    fetchShortcuts,
    addShortcut as apiAddShortcut,
    removeShortcut as apiRemoveShortcut,
} from "../../api/shortcuts";
import api from "../../api";
import { savePref, deletePref } from "../../helpers/prefs_api";
import { getPunchLocation } from "../../utils/terminalMap";
import { inkFor, tintFor } from "../../utils/themeColor";
import useAuth from "../Personal Attendance/useAuth";
/**
 * Dashboard Home — the SSW landing page.
 *
 * Layout (top to bottom):
 *   1. Hero welcome card — purple gradient with avatar, name, role badge,
 *      date, and quick-search input.
 *   2. Stats row — 4 tiles (Attendance / EPOs / Leaves / Tasks) with
 *      "view all" link + secondary info on the footer.
 *   3. Info row — 4 cards (Announcements / Important Dates / Latest News
 *      / Upcoming Events), each scrollable list of dated items.
 *   4. Spotlight row — 2 cards (Company Highlights / Employee Spotlight).
 *   5. Quick Actions — 5 shortcut tiles (Create EPO, Create ECN, Contract
 *      Review, Digital Inspection, View Display Board).
 *   6. My Shortcuts (user-pinned) + Recently Visited (last 5 clicks).
 *   7. Departments — 6 tiles (Engineering / Quality / HR / Finance-Admin /
 *      Operations / IT).
 *
 * ───────────────────────────────────────────────────────────────────────
 * BACKEND DATA SOURCES
 * ───────────────────────────────────────────────────────────────────────
 * Wired (graceful fallback to "—" on error):
 *   - Open Approvals  (EPOs stat secondary) → GET /api/epo/awaiting/:userId
 *   - Open Tasks      (Tasks stat)          → GET /api/TaskManagement/my-tasks
 *                                              ?tab=unstarted&empId=:userId
 *
 * Wired to localStorage:
 *   - Recent Documents count → `sswHome:recentDocs` (populated by DMS via
 *     the exported recordDocumentVisit() helper).
 *   - My Shortcuts → `sswHome:shortcuts`
 *   - Recently Visited → `sswHome:recents`
 *
 * Stub / placeholder (with clear comments where to wire real endpoints):
 *   - Attendance stat — needs an attendance endpoint (e.g.
 *     GET /api/attendance/today?empId=:userId). Today shows a dash.
 *   - Leaves stat — needs an FTO-balance endpoint (e.g.
 *     GET /api/fto/balance?empId=:userId). Today shows a dash.
 *   - Announcements / Important Dates / Latest News / Upcoming Events —
 *     each rendered from a `const` array near the top of this file. To
 *     drive these from a CMS, swap each array for a useEffect+useState
 *     pair that fetches from your endpoint.
 *   - Company Highlights / Employee Spotlight — same approach: static
 *     placeholder content today, easy to swap for real data later.
 *
 * ───────────────────────────────────────────────────────────────────────
 * MOUNTING THIS PAGE
 * ───────────────────────────────────────────────────────────────────────
 *   import Home from "../pages/Home";
 *   ...
 *   { path: "/dashboard", component: <Home /> },
 */

// ─────────────────────────────────────────────────────────────────────
// localStorage helpers
// ─────────────────────────────────────────────────────────────────────
// Round 9 (2026): shortcuts now persisted server-side via /api/shortcuts —
// no localStorage key here. Recents + recent-docs remain per-browser.
const LS_RECENTS = "sswHome:recents";
const LS_RECENT_DOCS = "sswHome:recentDocs";
const MAX_RECENTS = 5;
const MAX_RECENT_DOCS = 5;

const readJSON = (key, fallback) => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return fallback;
        return JSON.parse(raw) ?? fallback;
    } catch { return fallback; }
};

const writeJSON = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
};

/** Exported for DMS callers — see header comment block. */
const recordDocumentVisit = (doc) => {
    if (!doc || !doc.id) return;
    const list = readJSON(LS_RECENT_DOCS, []);
    const dedup = list.filter((d) => String(d.id) !== String(doc.id));
    dedup.unshift({ ...doc, ts: Date.now() });
    writeJSON(LS_RECENT_DOCS, dedup.slice(0, MAX_RECENT_DOCS));
};

// ─────────────────────────────────────────────────────────────────────
// STATIC CONFIG — replace these arrays with API fetches when you have
// ─────────────────────────────────────────────────────────────────────
// Curated shortcut catalog for the Add-Shortcut picker
// ─────────────────────────────────────────────────────────────────────
const SHORTCUT_CATALOG = [
    { key: "display-boards", label: "Display Boards", icon: "ri-tv-2-line", tone: "info", to: "/display-boards" },
    { key: "document-management", label: "Document Management", icon: "ri-folder-2-line", tone: "primary", to: "/document-management" },
    { key: "contract-review", label: "Contract Review", icon: "ri-file-text-line", tone: "success", to: "/contract-review" },
    { key: "epo", label: "EPO", icon: "ri-money-dollar-circle-line", tone: "warning", to: "/epo" },
    { key: "di", label: "Digital Inspection", icon: "ri-shield-check-line", tone: "danger", to: "/di" },
    { key: "task-management", label: "Task Management", icon: "ri-clipboard-line", tone: "primary", to: "/task-management" },
    { key: "attendance", label: "Personal Attendance", icon: "ri-calendar-2-line", tone: "info", to: "/attendance" },
    { key: "humanresource", label: "Human Resource", icon: "ri-team-line", tone: "secondary", to: "/humanresource" },
    { key: "kpi", label: "KPI", icon: "ri-bar-chart-line", tone: "info", to: "/kpi" },
    { key: "my-training", label: "My Training", icon: "ri-graduation-cap-line", tone: "success", to: "/dms/my-training" },
    { key: "mafia", label: "Mafia", icon: "ri-route-line", tone: "dark", to: "/mafia" },
    { key: "organizational-chart", label: "Org Chart", icon: "ri-organization-chart", tone: "secondary", to: "/organizational-chart" },
    { key: "production", label: "Production", icon: "ri-hammer-line", tone: "warning", to: "/production" },
    { key: "quality", label: "Quality", icon: "ri-shield-check-line", tone: "success", to: "/quality" },
    { key: "sales", label: "Sales", icon: "ri-store-2-line", tone: "info", to: "/sales" },
    { key: "shipping", label: "Shipping", icon: "ri-truck-line", tone: "primary", to: "/shipping" },
    { key: "supervisor", label: "Supervisor", icon: "ri-user-star-line", tone: "info", to: "/supervisor" },
    { key: "engineering", label: "Engineering", icon: "ri-tools-line", tone: "primary", to: "/engineering" },
    { key: "shukla", label: "Shukla", icon: "ri-building-2-line", tone: "info", to: "/shukla" },
    { key: "accounting", label: "Accounting / Finance", icon: "ri-bank-line", tone: "success", to: "/accounting" },
    { key: "it", label: "IT", icon: "ri-server-line", tone: "secondary", to: "/it" },
    // Round PREF-1 — owner: "user should be able [to] drag and drop more
    // menu[s]". Every remaining menu page is now in the Add-Shortcut
    // catalog; added tiles live in the My Shortcuts grid section, which is
    // already drag-and-drop (react-grid-layout, Round 28).
    { key: "workflows", label: "Workflows", icon: "ri-apps-2-line", tone: "primary", to: "/workflows" },
    { key: "missing-epo", label: "Missing-EPO Shipments", icon: "ri-inbox-unarchive-line", tone: "warning", to: "/epo/missing-epo" },
    { key: "travel-expense", label: "Travel Expense Approval", icon: "ri-flight-takeoff-line", tone: "info", to: "/travel-expense" },
    { key: "project-approval", label: "Project Approval", icon: "ri-briefcase-4-line", tone: "primary", to: "/projects/awaiting-approval" },
    { key: "time-attendance", label: "Time and Attendance", icon: "ri-time-line", tone: "success", to: "/time-and-attendance" },
    { key: "expected-visitors", label: "Expected Visitors", icon: "ri-user-follow-line", tone: "warning", to: "/visitors" },
    { key: "support", label: "Support", icon: "ri-customer-service-2-line", tone: "info", to: "/support" },
    { key: "my-profile", label: "My Profile", icon: "ri-user-3-line", tone: "secondary", to: "/profile/me" },
    { key: "planning", label: "Planning", icon: "ri-calendar-todo-line", tone: "info", to: "/planning" },
    { key: "perflexion", label: "Perflexion", icon: "ri-pulse-line", tone: "danger", to: "/perflexion" },
];

const QUICK_ACTIONS = [
    { key: "create-epo", label: "Create EPO", subtitle: "New Expense Purchase Order", icon: "ri-add-circle-line", tone: "warning", to: "/epo" },
    { key: "create-ecn", label: "Create ECN", subtitle: "Engineering Change Notice", icon: "ri-file-add-line", tone: "primary", to: "/dms/create/ecn" },
    { key: "contract-review", label: "Contract Review", subtitle: "New CR or browse existing", icon: "ri-file-text-line", tone: "success", to: "/contract-review/new" },
    { key: "digital-inspection", label: "Digital Inspection", subtitle: "Open the DI workspace", icon: "ri-shield-check-line", tone: "danger", to: "/di" },
    { key: "display-board", label: "View Display Board", subtitle: "Live shop-floor status", icon: "ri-tv-2-line", tone: "info", to: "/display-boards" },
];

const DEPARTMENTS = [
    { key: "engineering", label: "Engineering", subtitle: "Drawings, ECNs, R&D", icon: "ri-tools-line", tone: "primary", to: "/engineering" },
    { key: "quality", label: "Quality", subtitle: "Inspection, DI, audits", icon: "ri-shield-check-line", tone: "success", to: "/quality" },
    { key: "hr", label: "HR", subtitle: "Employees, FTO, reviews", icon: "ri-team-line", tone: "secondary", to: "/humanresource" },
    { key: "finance-admin", label: "Finance / Admin", subtitle: "Accounting, IT admin", icon: "ri-bank-line", tone: "info", to: "/accounting" },
    { key: "operations", label: "Operations", subtitle: "Production, planning, shipping", icon: "ri-hammer-line", tone: "warning", to: "/production" },
    { key: "it", label: "IT", subtitle: "Systems, roles, access", icon: "ri-server-line", tone: "dark", to: "/it" },
];

// ─────────────────────────────────────────────────────────────────────
// Welcome helpers
// ─────────────────────────────────────────────────────────────────────
const readAuthUser = () => {
    try { return JSON.parse(sessionStorage.getItem("authUser") || "{}"); }
    catch { return {}; }
};

const readWelcomeName = (obj) =>
    obj?.username
    || obj?.first_name
    || obj?.firstName
    || obj?.name
    || obj?.email
    || obj?.data?.first_name
    || "there";

const readWelcomeRole = (obj) =>
    obj?.role || obj?.jobTitle || obj?.title || null;

const computeInitials = (name) => {
    if (!name || typeof name !== "string") return "U";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "U";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const greetingForHour = (h = new Date().getHours()) => {
    if (h < 12) return "Good morning,";
    if (h < 17) return "Good afternoon,";
    return "Good evening,";
};

const formatToday = (d = new Date()) =>
    d.toLocaleDateString(undefined, {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
    });

const formatRelative = (ts) => {
    if (!ts) return "";
    const diff = Math.max(0, Date.now() - ts);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return "yesterday";
    if (days < 7) return `${days}d ago`;
    return new Date(ts).toLocaleDateString();
};

// ─────────────────────────────────────────────────────────────────────
// Inline components — kept here for cohesion. Extract later if you want.
// ─────────────────────────────────────────────────────────────────────

/**
 * Hero StatCard — the prettier stat tile design from the existing
 * dashboard. White card, label top-left, colored icon disc top-right,
 * big number, footer row with a router link + secondary info.
 */
const ResponsiveGridLayout = RGLPkg.WidthProvider
    ? RGLPkg.WidthProvider(RGLPkg.Responsive)
    : RGLPkg.ResponsiveGridLayout;

// Round 28 — default layout mirrors the pre-grid arrangement exactly.
const HOME_LAYOUT_KEY = "ssw:homeLayout:v1";
const DEFAULT_HOME_LAYOUTS = {
    lg: [
        { i: "stat-attendance", x: 0, y: 0,  w: 3,  h: 4, minW: 2, minH: 3 },
        { i: "stat-epos",       x: 3, y: 0,  w: 3,  h: 4, minW: 2, minH: 3 },
        { i: "stat-leaves",     x: 6, y: 0,  w: 3,  h: 4, minW: 2, minH: 3 },
        { i: "stat-tasks",      x: 9, y: 0,  w: 3,  h: 4, minW: 2, minH: 3 },
        { i: "announcements",   x: 0, y: 4,  w: 12, h: 9, minW: 4, minH: 5 },
        { i: "shortcuts",       x: 0, y: 13, w: 8,  h: 10, minW: 4, minH: 5 },
        { i: "recents",         x: 8, y: 13, w: 4,  h: 10, minW: 3, minH: 5 },
    ],
};
// India accounts (owner decision 2026-09-29): people of the India company
// use MAFIA Aux, Digital Inspection, DMS and their tasks. The time clock,
// purchase orders and time off are Florida features, so their three cards
// are not part of an India account's home page. Same sizes, no gap.
const INDIA_HOME_LAYOUTS = {
    lg: [
        { i: "stat-tasks",      x: 0, y: 0,  w: 3,  h: 4, minW: 2, minH: 3 },
        { i: "announcements",   x: 0, y: 4,  w: 12, h: 9, minW: 4, minH: 5 },
        { i: "shortcuts",       x: 0, y: 13, w: 8,  h: 10, minW: 4, minH: 5 },
        { i: "recents",         x: 8, y: 13, w: 4,  h: 10, minW: 3, minH: 5 },
    ],
};
// Pages an India account can open (the Add-Shortcut picker offers only these).
const INDIA_SHORTCUT_KEYS = [
    "document-management", "di", "mafia", "task-management", "my-training", "support", "my-profile",
];
// A layout saved while the account was a Florida account keeps the Tasks
// card in the fourth column. For an India account the three cards before it
// are gone, so the Tasks card moves to the first column of its row.
const layoutForIndia = (layouts) => {
    if (!layouts || typeof layouts !== "object") return layouts;
    const out = {};
    for (const [bp, items] of Object.entries(layouts)) {
        out[bp] = Array.isArray(items)
            ? items.map((it) => (it && it.i === "stat-tasks" ? { ...it, x: 0 } : it))
            : items;
    }
    return out;
};
const loadHomeLayouts = (indiaOnly = false) => {
    try {
        const raw = localStorage.getItem(HOME_LAYOUT_KEY);
        if (raw) return indiaOnly ? layoutForIndia(JSON.parse(raw)) : JSON.parse(raw);
    } catch (e) { /* corrupted layout → fall back to default */ }
    return indiaOnly ? INDIA_HOME_LAYOUTS : DEFAULT_HOME_LAYOUTS;
};

// Round THEME-2: tinted disc + ink follow the theme (original colours in Light · Classic).
const STAT_TONE_HEX = {
    primary: { bg: tintFor("#eef1fa"), fg: inkFor("#405189") },
    info: { bg: tintFor("#dff3fa"), fg: inkFor("#299cdb") },
    success: { bg: tintFor("#daf4f0"), fg: inkFor("#0ab39c") },
    warning: { bg: tintFor("#fef4e4"), fg: inkFor("#f7b84b") },
    danger: { bg: tintFor("#fde8e4"), fg: inkFor("#f06548") },
    secondary: { bg: tintFor("#eceef1"), fg: inkFor("#74788d") },
};

// Round PROFILE-1 — shortcut tiles reworked as WIDGET CARDS in the same
// visual family as the stat row above (Attendance / EPOs / Tasks / FTO):
// uppercase label, tinted icon chip, bordered footer with an "Open" link.
// (Owner: "instead of shortcut like this.. can we card like above for
// attendance".) Persistence is unchanged — shortcuts live in the per-user
// /api/shortcuts store, so they survive refresh and roam across machines.
const ShortcutStatCard = ({ icon, label, tone = "primary", to, onOpen, onRemove }) => {
    const { bg, fg } = STAT_TONE_HEX[tone] || STAT_TONE_HEX.primary;
    return (
        <Card className="shadow-sm border-0 h-100 position-relative">
            <CardBody className="p-3">
                <div className="d-flex align-items-start justify-content-between mb-2">
                    <div
                        className="text-uppercase fw-semibold text-muted text-truncate"
                        style={{ fontSize: 11, letterSpacing: ".5px", paddingRight: 28 }}
                        title={label}
                    >
                        {label}
                    </div>
                    <span
                        className="d-inline-flex align-items-center justify-content-center ssw-stat-icon"
                        style={{ width: 38, height: 38, borderRadius: 8, background: bg, color: fg, flexShrink: 0 }}
                        aria-hidden="true"
                    >
                        <i className={icon} style={{ fontSize: 20 }} />
                    </span>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-3 pt-2 border-top" style={{ fontSize: 12 }}>
                    <Link
                        to={to}
                        className="text-primary text-decoration-none fw-medium stretched-link"
                        onClick={onOpen}
                    >
                        Open <i className="ri-arrow-right-line align-bottom"></i>
                    </Link>
                </div>
            </CardBody>
            {onRemove && (
                <Button
                    color="light"
                    size="sm"
                    onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); onRemove(); }}
                    title={`Remove ${label} from shortcuts`}
                    aria-label={`Remove ${label} from shortcuts`}
                    style={{
                        position: "absolute", top: 6, right: 6,
                        width: 24, height: 24, padding: 0, lineHeight: 1,
                        borderRadius: "50%", opacity: 0.85, zIndex: 2,
                    }}
                >
                    <i className="ri-close-line"></i>
                </Button>
            )}
        </Card>
    );
};

const HeroStatCard = ({
    label, value, icon, tone = "primary",
    linkTo, linkLabel,
    note, noteTone = "secondary",
    loading,
    action, // Round TA-1 — optional action node (e.g. Clock In / Clock Out button)
    subNote, // Round TA-1b — small muted line under the value (e.g. In/Out times)
}) => {
    const { bg, fg } = STAT_TONE_HEX[tone] || STAT_TONE_HEX.primary;
    return (
        <Card className="shadow-sm border-0 h-100">
            <CardBody className="p-3">
                <div className="d-flex align-items-start justify-content-between mb-2">
                    <div className="text-uppercase fw-semibold text-muted" style={{ fontSize: 11, letterSpacing: ".5px" }}>
                        {label}
                    </div>
                    <span
                        className="d-inline-flex align-items-center justify-content-center ssw-stat-icon"
                        style={{
                            width: 38, height: 38, borderRadius: 8,
                            background: bg, color: fg, flexShrink: 0,
                        }}
                        aria-hidden="true"
                    >
                        <i className={icon} style={{ fontSize: 20 }} />
                    </span>
                </div>
                <div className="d-flex align-items-center justify-content-between gap-2">
                    <div className="fw-bold" style={{ fontSize: 28, lineHeight: 1.2, color: "var(--ssw-text-1, #212529)" }}>
                        {loading ? <span className="placeholder col-3 bg-secondary opacity-25 rounded"></span> : value}
                    </div>
                    {action || null}
                </div>
                {subNote ? (
                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>{subNote}</div>
                ) : null}
                <div className="d-flex align-items-center justify-content-between mt-3 pt-2 border-top" style={{ fontSize: 12 }}>
                    {linkTo ? (
                        <Link to={linkTo} className="text-primary text-decoration-none fw-medium">
                            {linkLabel || "View all"}
                        </Link>
                    ) : (
                        <span className="text-muted">{linkLabel || ""}</span>
                    )}
                    {note ? (
                        <span className={`fw-medium text-${noteTone}`}>{note}</span>
                    ) : null}
                </div>
            </CardBody>
        </Card>
    );
};

// Round DIR-1 (2026-10-03): "Next holiday" / "Your next day off" lines for the FTO card.
// Dates come as yyyy-MM-dd and are read as local calendar days (no time-zone shift).
const dayLabel = (ymd) => {
    if (!ymd) return "";
    const [y, m, d] = String(ymd).split("-").map(Number);
    const day = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((day - today) / 86400000);
    if (diff === 0) return "Today";
    if (diff === 1) return "Tomorrow";
    return day.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};
const upcomingLines = (u) => {
    if (!u || (!u.nextHoliday && !u.nextDayOff)) return null;
    const h = u.nextHoliday;
    const o = u.nextDayOff;
    return (
        <>
            {h ? (
                <div className="text-truncate" title={`Next holiday: ${h.name}`}>
                    Next holiday: {dayLabel(h.date)}{h.name ? ` · ${h.name}` : ""}
                </div>
            ) : null}
            {o ? (
                <div className="text-truncate">
                    Your next day off: {dayLabel(o.startDate)}{o.endDate ? ` to ${dayLabel(o.endDate)}` : ""}{o.halfDay ? " · half day" : ""}
                </div>
            ) : null}
        </>
    );
};

/**
 * Module-level constants used by the welcome card subtree.
 */

// ─────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────
const Home = () => {
    document.title = "Dashboard | SSW Technologies";
    const navigate = useNavigate();

    const authUser = useMemo(() => readAuthUser(), []);
    const userId = authUser?.uid;
    const welcomeName = useMemo(() => readWelcomeName(authUser), [authUser]);
    const welcomeRole = authUser?.title || authUser?.role || null;
    const initials = useMemo(() => computeInitials(welcomeName), [welcomeName]);
    const greeting = useMemo(() => greetingForHour(), []);
    const today = useMemo(() => formatToday(), []);

    // India accounts: no time clock, no purchase orders, no time off (the
    // server decides who is one; an IT Admin is never limited).
    const auth = useAuth();
    const indiaOnly = !!auth.isIndiaAccount && !auth.isItAdmin;

    // ── live stats ────────────────────────────────────────────────────
    const [openApprovals, setOpenApprovals] = useState(null);
    const [openTasks, setOpenTasks] = useState(null);
    // Round TA-1 — Leaves card: net available FTO days (base earned minus
    // used — the same formula MyFtoBalance uses; base already includes comp
    // + carried, per the Round 7 G-49 fix). null = loading, undefined = the
    // endpoint failed → card shows a dash instead of a stale number.
    const [ftoBalance, setFtoBalance] = useState(null);
    // Round DIR-1 (2026-10-03, owner): next company holiday + my next approved day off,
    // shown on the FTO card (GET schedule/upcoming, self only, Florida people only).
    const [upcoming, setUpcoming] = useState(null);
    const [statsError, setStatsError] = useState(false);

    // ── client-side state ─────────────────────────────────────────────
    // Round 9: shortcuts moved from localStorage to /api/shortcuts so
    // they follow the user across browsers/devices. `shortcuts` is now
    // an array of server DTOs ({id, shortcutKey, path, label, icon,
    // sortOrder}). Earlier code stored {key, to, label, icon}; the
    // helpers below adapt between the two shapes so the UI didn't have
    // to change.
    const [shortcuts, setShortcuts] = useState([]);
    const [shortcutsLoaded, setShortcutsLoaded] = useState(false);
    const [recents, setRecents] = useState(() => readJSON(LS_RECENTS, []));
    const [recentDocs] = useState(() => readJSON(LS_RECENT_DOCS, []));
    const [query, setQuery] = useState("");

    // Round 28 — customizable grid state (persisted per user).
    const [editLayout, setEditLayout] = React.useState(false);
    const [homeLayouts, setHomeLayouts] = React.useState(() => loadHomeLayouts(indiaOnly));
    // Round 40 hotfix1 — first paint can beat the server prefs fetch on a
    // fresh browser; when the roamed layout arrives, apply it live.
    React.useEffect(() => {
        const onPrefs = (e) => {
            const raw = e?.detail?.homeLayout;
            if (raw) {
                try {
                    const parsed = JSON.parse(raw);
                    if (parsed && typeof parsed === "object") setHomeLayouts(indiaOnly ? layoutForIndia(parsed) : parsed);
                } catch (err) { /* corrupted server value — keep local */ }
            }
            // Round PREF-1 — apply roamed Recently Visited when it lands
            // after mount (fresh browser: first paint beats the prefs fetch).
            const rawRecents = e?.detail?.recents;
            if (rawRecents) {
                try {
                    const parsed = JSON.parse(rawRecents);
                    if (Array.isArray(parsed)) setRecents(parsed);
                } catch (err) { /* corrupted server value — keep local */ }
            }
        };
        window.addEventListener("ssw:prefsSynced", onPrefs);
        return () => window.removeEventListener("ssw:prefsSynced", onPrefs);
    }, [indiaOnly]);
    const onHomeLayoutChange = (_l, all) => {
        setHomeLayouts(all);
        try { localStorage.setItem(HOME_LAYOUT_KEY, JSON.stringify(all)); } catch (e) { /* storage full/blocked — layout just won't persist */ }
        savePref("homeLayout", JSON.stringify(all)); // Round 40 — roams across machines
    };
    const resetHomeLayout = () => {
        setHomeLayouts(indiaOnly ? INDIA_HOME_LAYOUTS : DEFAULT_HOME_LAYOUTS);
        try { localStorage.removeItem(HOME_LAYOUT_KEY); } catch (e) { /* ignore */ }
        deletePref("homeLayout"); // Round 40 — reset roams too
    };
    const [pickerOpen, setPickerOpen] = useState(false);


    // ── Attendance card (Round TA-1) ──────────────────────────────────
    //
    // Driven by GET /api/WhosInBuilding/my-status — the same server-side
    // state the Time & Attendance page and the punch endpoints validate
    // against (identity comes from the JWT; no empId is sent). The card
    // shows the current status AND the one safe next action:
    //
    //   OUT (day)          → Clock In
    //   IN, lunch OUT      → Back Lunch
    //   IN, business OUT   → Back Biz
    //   IN, personal OUT   → Back Personal
    //   IN (no open break) → Clock Out
    //   unknown            → no button (never show an unsafe action)
    //
    // The POSTs are the exact endpoints the Time & Attendance page uses
    // (WhosInBuilding/clock-in, clock-out, clock-in-for-lunch, …) — no
    // duplicate punch logic lives in Home. The backend re-validates the
    // punch state server-side and ignores any client-claimed identity.

    const [attendanceInfo, setAttendanceInfo] = useState({
        status: null,       // null = unknown → button hidden
        lunchStatus: null,
        businessStatus: null,
        personalStatus: null,
        location: "",
        loaded: false,
    });
    const [punchBusy, setPunchBusy] = useState(false);
    const [punchMsg, setPunchMsg] = useState(null); // { ok, text }
    // Round TA-6: grace-window undo info from my-status.
    const [homeUndo, setHomeUndo] = useState(null); // { lastTime, windowMin }

    const authUserData = sessionStorage.getItem("authUser");

    const empId = authUserData
        ? JSON.parse(authUserData)?.uid
        : null;

    const fetchAttendance = useCallback(async () => {
        try {
            const response = await api.get("WhosInBuilding/my-status");
            const d = response.data || {};

            // Location label from the last punch of today, if any.
            const last = Array.isArray(d.todayPunches) && d.todayPunches.length
                ? d.todayPunches[d.todayPunches.length - 1]
                : null;
            const locationInfo = getPunchLocation(
                last?.terminalId ?? last?.TerminalId,
                last?.computer ?? last?.Computer ?? "",
                last?.remote ?? last?.Remote,
                d.officeNetworksKnown === true
            );

            // A Break/Business/Personal "OUT" only means "currently away" if
            // the punch happened today — a lunch-out from a previous day that
            // was never followed by a back-punch must not resurface as an
            // active break this morning.
            const isToday = (ts) => {
                if (!ts) return false;
                const dte = new Date(ts);
                const now = new Date();
                return dte.getFullYear() === now.getFullYear()
                    && dte.getMonth() === now.getMonth()
                    && dte.getDate() === now.getDate();
            };

            const newest = Array.isArray(d.todayPunches) && d.todayPunches.length
                ? d.todayPunches[d.todayPunches.length - 1]
                : null;
            setHomeUndo(newest ? {
                lastTime: newest.clockTime ?? newest.ClockTime,
                windowMin: d.undoWindowMinutes || 10,
            } : null);

            setAttendanceInfo({
                status: d.status || "OUT",
                lunchStatus: d.lunchStatus === "OUT" && isToday(d.lunchTime) ? "OUT" : d.lunchStatus ?? null,
                lunchActive: d.lunchStatus === "OUT" && isToday(d.lunchTime),
                businessActive: d.businessStatus === "OUT" && isToday(d.businessTime),
                personalActive: d.personalStatus === "OUT" && isToday(d.personalTime),
                clockInTime: d.clockInTime || null,   // today's first Normal IN
                clockOutTime: d.clockOutTime || null, // today's last Normal OUT
                location: locationInfo.label,
                loaded: true,
            });
        } catch (error) {
            console.error("Attendance fetch error:", error);
            // Leave status null/unknown — the action button stays hidden and
            // the card shows a dash instead of a wrong state.
            setAttendanceInfo((p) => ({ ...p, loaded: true }));
        }
    }, []);

    useEffect(() => {
        // The time clock is a Florida feature: an India account has no card to fill.
        if (empId && !indiaOnly) fetchAttendance();
    }, [empId, fetchAttendance, indiaOnly]);

    // Next safe punch action derived from server state only.
    const nextPunchAction = useMemo(() => {
        const { status, lunchActive, businessActive, personalActive } = attendanceInfo;
        // NOTE: the Clock* request DTOs declare non-nullable EmpId under
        // <Nullable>enable</Nullable>, so ASP.NET model validation rejects a
        // body without it (auto-400) before the controller runs. We send our
        // own uid — the backend independently verifies it matches the JWT and
        // returns 403 on any mismatch, so this is contract compliance, not trust.
        if (status === "OUT") {
            return { label: "Clock In", color: "success", url: "WhosInBuilding/clock-in", body: { empId, myType: "tod" } };
        }
        if (status === "IN") {
            if (lunchActive)
                return { label: "Back Lunch", color: "info", url: "WhosInBuilding/clock-in-for-lunch", body: { empId } };
            if (businessActive)
                return { label: "Back Biz", color: "warning", url: "WhosInBuilding/clock-in-for-business", body: { empId } };
            if (personalActive)
                return { label: "Back Personal", color: "secondary", url: "WhosInBuilding/clock-in-for-personal", body: { empId } };
            return { label: "Clock Out", color: "danger", url: "WhosInBuilding/clock-out", body: { empId } };
        }
        return null; // unknown state → no unsafe button
    }, [attendanceInfo, empId]);

    const handleHomePunch = useCallback(async () => {
        if (!nextPunchAction || punchBusy) return;
        setPunchBusy(true);
        setPunchMsg(null);
        try {
            await api.post(nextPunchAction.url, nextPunchAction.body);
            setPunchMsg({ ok: true, text: `${nextPunchAction.label} recorded.` });
            // Invalidate the Time & Attendance page's cached last-action state
            // so it refetches fresh server data on next visit.
            ["getLastAction", "getLastActionBreak", "getLastActionBusiness", "getLastActionPersonal"]
                .forEach((k) => { try { sessionStorage.removeItem(k); } catch (e) { /* ignore */ } });
            await fetchAttendance();
        } catch (error) {
            const d = error?.response?.data;
            const text =
                (typeof d === "string" && d) ||
                d?.message ||
                d?.title ||               // ASP.NET ProblemDetails
                (d?.errors && Object.values(d.errors).flat()[0]) || // ValidationProblemDetails
                "Punch failed — please try again.";
            setPunchMsg({ ok: false, text });
            await fetchAttendance(); // resync — state may have changed elsewhere
        } finally {
            setPunchBusy(false);
        }
    }, [nextPunchAction, punchBusy, fetchAttendance]);

    // Round TA-6: undo the punch just made (grace window; server re-validates).
    const handleHomeUndo = useCallback(async () => {
        if (punchBusy) return;
        setPunchBusy(true);
        try {
            const res = await api.post("WhosInBuilding/undo-last-punch", {});
            setPunchMsg({ ok: true, text: res?.data?.message || "Punch undone." });
            await fetchAttendance();
        } catch (error) {
            const d = error?.response?.data;
            setPunchMsg({ ok: false, text: (typeof d === "string" && d) || d?.message || "Undo failed." });
        } finally {
            setPunchBusy(false);
        }
    }, [punchBusy, fetchAttendance]);

    const homeUndoAvailable = homeUndo &&
        (Date.now() - new Date(homeUndo.lastTime).getTime()) < homeUndo.windowMin * 60000;


    // ── server load: shortcuts ────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const list = await fetchShortcuts();
                if (!cancelled) setShortcuts(list);
            } catch {
                // 401/500 → leave list empty; UI shows "No shortcuts yet"
            } finally {
                if (!cancelled) setShortcutsLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // ── load live stats ───────────────────────────────────────────────
    const loadStats = useCallback(async () => {
        if (!userId) return;
        setStatsError(false);

        // India accounts: only the Tasks card exists (see INDIA_HOME_LAYOUTS),
        // so the purchase-order and time-off figures are not asked for.
        if (!indiaOnly) {
        try {
            const res = await api.get(`epo/awaiting/${userId}`);
            const arr = Array.isArray(res.data?.results)
                ? res.data.results
                : Array.isArray(res.data) ? res.data : [];
            setOpenApprovals(arr.length);
        } catch {
            setOpenApprovals(0);
            setStatsError(true);
        }
        }

        try {
            const res = await api.get("/TaskManagement/my-tasks", {
                params: { tab: "unstarted", empId: userId },
            });
            const payload = res?.data ?? res;
            const rows = Array.isArray(payload) ? payload
                : Array.isArray(payload?.items) ? payload.items
                    : Array.isArray(payload?.data) ? payload.data : [];
            setOpenTasks(rows.length);
        } catch {
            setOpenTasks(0);
            setStatsError(true);
        }

        // Round TA-1 — Leaves card. Self-scoped: /api/fto/balances enforces
        // caller-may-only-read-self/subordinate/HR server-side (G-9), and we
        // only ever ask for our own. On failure the card shows "—" — never a
        // stale or made-up number.
        if (!indiaOnly) try {
            const res = await api.get("fto/balances", {
                params: { empId: userId, year: new Date().getFullYear() },
            });
            const b = res?.data;
            if (b && typeof b.baseFtoEarned === "number") {
                setFtoBalance({
                    net: b.baseFtoEarned - b.ftoDaysUsed,
                    scheduled: b.scheduledForFuture,
                });
            } else {
                setFtoBalance(undefined);
            }
        } catch {
            setFtoBalance(undefined); // graceful — dash, no error banner spam
        }

        if (!indiaOnly) {
            api.get("schedule/upcoming")
                .then((r) => setUpcoming(r?.data || null))
                .catch(() => setUpcoming(null));
        }
    }, [userId, indiaOnly]);

    useEffect(() => { loadStats(); }, [loadStats]);
    // Round 9: shortcuts now persisted via /api/shortcuts (handlers below)
    // — no localStorage write needed. Recents remain per-browser.
    useEffect(() => {
        writeJSON(LS_RECENTS, recents);
        // Round PREF-1 — Recently Visited follows the user across
        // browsers/machines (owner: "recently visited gone after refresh").
        // Skip the initial empty-state write so a fresh browser doesn't
        // wipe the server copy before syncPrefsAtStartup seeds it.
        if (recents.length > 0) savePref("recents", JSON.stringify(recents));
    }, [recents]);

    // ── shortcut + recents handlers ───────────────────────────────────
    // Round 9: shortcuts go through /api/shortcuts. We do optimistic
    // updates so the UI stays snappy; on error we roll the row back.
    // The catalog stores {key, to, label, icon}; the server stores
    // {id, shortcutKey, path, label, icon} — we map between them here.
    const addShortcut = async (entry) => {
        if (shortcuts.some((s) => s.shortcutKey === entry.key)) return;
        try {
            const saved = await apiAddShortcut(entry);
            setShortcuts((prev) =>
                prev.some((s) => s.id === saved.id) ? prev : [...prev, saved]
            );
        } catch {
            window.alert("Could not save shortcut. Please retry.");
        }
    };
    const removeShortcut = async (shortcutKey) => {
        const target = shortcuts.find((s) => s.shortcutKey === shortcutKey);
        if (!target) return;
        // optimistic: drop locally, then send DELETE; if it fails, restore.
        setShortcuts((prev) => prev.filter((s) => s.id !== target.id));
        try {
            await apiRemoveShortcut(target.id);
        } catch {
            setShortcuts((prev) => [...prev, target]);
            window.alert("Could not remove shortcut. Please retry.");
        }
    };
    const recordVisit = useCallback((entry) => {
        if (!entry?.to) return;
        setRecents((prev) => {
            const dedup = prev.filter((r) => r.to !== entry.to);
            return [{ ...entry, ts: Date.now() }, ...dedup].slice(0, MAX_RECENTS);
        });
    }, []);
    const clearRecents = () => setRecents([]);

    // ── quick search ──────────────────────────────────────────────────
    const q = query.trim().toLowerCase();
    const handleSearchKeyDown = (e) => {
        if (e.key !== "Enter" || !q) return;
        const hit =
            SHORTCUT_CATALOG.find((c) => c.label.toLowerCase().includes(q))
            || DEPARTMENTS.find((d) => d.label.toLowerCase().includes(q))
            || QUICK_ACTIONS.find((a) => a.label.toLowerCase().includes(q));
        if (hit?.to) {
            recordVisit({ to: hit.to, label: hit.label, icon: hit.icon });
            navigate(hit.to);
        }
    };

    // ── visible (filtered) lists ──────────────────────────────────────
    // An India account: a shortcut pinned earlier to a page that is closed
    // now is not shown (it stays saved and comes back on the way back).
    const visibleShortcuts = useMemo(
        () => shortcuts
            .filter((s) => !indiaOnly || INDIA_SHORTCUT_KEYS.includes(s.shortcutKey))
            .filter((s) => !q || (s.label || "").toLowerCase().includes(q)),
        [shortcuts, q, indiaOnly]
    );
    const visibleRecents = useMemo(
        () => q ? recents.filter((r) => (r.label || "").toLowerCase().includes(q)) : recents,
        [recents, q]
    );
    const visibleDepartments = useMemo(
        () => q ? DEPARTMENTS.filter((d) => d.label.toLowerCase().includes(q)) : DEPARTMENTS,
        [q]
    );
    const pickerCatalog = useMemo(
        () => SHORTCUT_CATALOG
            .filter((c) => !indiaOnly || INDIA_SHORTCUT_KEYS.includes(c.key))
            .filter((c) => !shortcuts.some((s) => s.shortcutKey === c.key)),
        [shortcuts, indiaOnly]
    );

    return (
        <div className="page-content">
            <Container fluid>

                {/* ════════════════════════════════════════════════════
                    HERO WELCOME CARD — purple gradient
                ════════════════════════════════════════════════════ */}
                <Card
                    className="border-0 mb-3 text-white"
                    style={{
                        background: "linear-gradient(135deg, #5b50d6 0%, #3a3287 60%, #2c2076 100%)",
                        boxShadow: "0 8px 24px rgba(58, 50, 135, 0.25)",
                        borderRadius: 14,
                    }}
                >
                    <CardBody className="p-4">
                        <Row className="align-items-center g-3">
                            <Col lg={8} md={12}>
                                <div className="d-flex align-items-center gap-3 flex-wrap">
                                    {/* Avatar */}
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                                        style={{
                                            width: 72, height: 72,
                                            background: "rgba(255,255,255,0.18)",
                                            border: "2px solid rgba(255,255,255,0.28)",
                                            color: "#fff",
                                            fontSize: 24,
                                            letterSpacing: 1,
                                            flexShrink: 0,
                                        }}
                                        aria-hidden="true"
                                    >
                                        {initials}
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ opacity: 0.9, fontSize: 14 }}>{greeting}</div>
                                        <h2 className="text-white mb-1 fw-bold" style={{ fontSize: 30, lineHeight: 1.15 }}>
                                            {welcomeName}!
                                        </h2>
                                        <div style={{ opacity: 0.85, fontSize: 14 }}>
                                            Welcome back to your dashboard
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col lg={4} md={12} className="text-lg-end text-md-start">
                                {welcomeRole ? (
                                    <Badge
                                        pill
                                        className="px-3 py-2 mb-2 fw-medium"
                                        style={{
                                            background: "rgba(255,255,255,0.18)",
                                            color: "#fff",
                                            border: "1px solid rgba(255,255,255,0.3)",
                                            fontSize: 13,
                                        }}
                                    >
                                        {welcomeRole}
                                    </Badge>
                                ) : null}
                                <div style={{ opacity: 0.9, fontSize: 13 }}>
                                    <i className="ri-calendar-line align-bottom me-1"></i>
                                    {today}
                                </div>
                            </Col>
                        </Row>

                        {/* Round 26 (owner): the in-hero search box is removed —
                            the Ctrl+K command palette (Round 25) replaces it
                            app-wide. The query/filter machinery below stays in
                            place but inert (query remains empty). */}
                        <Row className="mt-3">
                            <Col>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => window.dispatchEvent(new Event("ssw:openPalette"))}
                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); window.dispatchEvent(new Event("ssw:openPalette")); } }}
                                    style={{ fontSize: 12.5, opacity: 0.85, cursor: "pointer" }}
                                    aria-label="Open quick navigation"
                                >
                                    <i className="ri-search-line align-bottom me-1"></i>
                                    Press <kbd style={{ background: "rgba(255,255,255,0.18)", borderRadius: 4, padding: "1px 6px" }}>Ctrl K</kbd> to jump to any page or find a colleague
                                </span>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* ════════════════════════════════════════════════════
                    STATS — 4 hero stat cards
                ════════════════════════════════════════════════════ */}
                {/* Round 28 — customizable dashboard: drag & resize in "Customize" mode; layout persists per user. */}
                <div className="d-flex justify-content-end align-items-center gap-2 mb-1">
                    {editLayout ? (
                        <button type="button" className="btn btn-sm btn-light" onClick={resetHomeLayout}>
                            <i className="ri-restart-line align-bottom me-1" />Reset layout
                        </button>
                    ) : null}
                    <button type="button" className={`btn btn-sm ${editLayout ? "btn-primary" : "btn-soft-secondary"}`} onClick={() => setEditLayout((v) => !v)}>
                        <i className={`${editLayout ? "ri-check-line" : "ri-layout-masonry-line"} align-bottom me-1`} />
                        {editLayout ? "Done" : "Customize layout"}
                    </button>
                </div>
                <ResponsiveGridLayout
                    className={`ssw-home-grid ${editLayout ? "ssw-home-grid--editing" : ""}`}
                    layouts={homeLayouts}
                    breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
                    cols={{ lg: 12, md: 12, sm: 6, xs: 1 }}
                    rowHeight={42}
                    margin={[12, 12]}
                    isDraggable={editLayout}
                    isResizable={editLayout}
                    onLayoutChange={onHomeLayoutChange}
                >
                    {/* India accounts: no time clock, no purchase orders, no time off
                        (owner decision 2026-09-29). Only the Tasks card is shown. */}
                    {!indiaOnly && (
                    <div key="stat-attendance" className="ssw-grid-item">
                        <HeroStatCard
                            label="Attendance"
                            value={attendanceInfo.status ?? "—"}
                            loading={!attendanceInfo.loaded}
                            icon="ri-time-line"
                            tone={
                                attendanceInfo.status === "IN"
                                    ? "success"
                                    : "danger"
                            }
                            linkTo="/time-and-attendance"
                            linkLabel="View Attendance"
                            subNote={
                                attendanceInfo.clockInTime
                                    ? `In ${new Date(attendanceInfo.clockInTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` +
                                      (attendanceInfo.status === "OUT" && attendanceInfo.clockOutTime
                                          ? ` \u00B7 Out ${new Date(attendanceInfo.clockOutTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                                          : "")
                                    : null
                            }
                            note={
                                punchMsg ? (
                                    <>
                                        {punchMsg.text}
                                        {homeUndoAvailable && (
                                            <a href="#undo" className="ms-2 fw-semibold"
                                               onClick={(e) => { e.preventDefault(); handleHomeUndo(); }}>
                                                Undo
                                            </a>
                                        )}
                                    </>
                                ) : attendanceInfo.location
                            }
                            noteTone={
                                punchMsg
                                    ? (punchMsg.ok ? "success" : "danger")
                                    : attendanceInfo.status === "IN"
                                        ? "success"
                                        : "danger"
                            }
                            action={
                                nextPunchAction ? (
                                    <Button
                                        size="sm"
                                        color={nextPunchAction.color}
                                        disabled={punchBusy}
                                        onClick={handleHomePunch}
                                        style={{ whiteSpace: "nowrap" }}
                                    >
                                        {punchBusy
                                            ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                            : nextPunchAction.label}
                                    </Button>
                                ) : null
                            }
                        />
                    </div>
                    )}
                    {!indiaOnly && (
                    <div key="stat-epos" className="ssw-grid-item">
                        <HeroStatCard
                            label="EPOs"
                            value={openApprovals ?? "—"}
                            loading={openApprovals == null}
                            icon="ri-money-dollar-circle-line"
                            tone="success"
                            linkTo="/epo"
                            linkLabel="View all EPOs"
                            note={openApprovals != null && openApprovals > 0
                                ? `${openApprovals} awaiting you`
                                : null}
                            noteTone="warning"
                        />
                    </div>
                    )}
                    {!indiaOnly && (
                    <div key="stat-leaves" className="ssw-grid-item">
                        <HeroStatCard
                            // owner request 2026-07: renamed from "Leaves" — label only
                            label="FTO"
                            value={
                                ftoBalance && typeof ftoBalance.net === "number"
                                    ? Number(ftoBalance.net.toFixed(2))
                                    : "—"
                            }
                            loading={ftoBalance === null}
                            icon="ri-calendar-2-line"
                            tone="warning"
                            linkTo="/attendance"
                            linkLabel="See details"
                            subNote={upcomingLines(upcoming)}
                            note={
                                ftoBalance && typeof ftoBalance.net === "number"
                                    ? (ftoBalance.scheduled > 0
                                        ? `FTO days left · ${ftoBalance.scheduled} scheduled`
                                        : "FTO days left")
                                    : null
                            }
                            noteTone="secondary"
                        />
                    </div>
                    )}
                    <div key="stat-tasks" className="ssw-grid-item">
                        <HeroStatCard
                            label="Tasks"
                            value={openTasks ?? "—"}
                            loading={openTasks == null}
                            icon="ri-checkbox-circle-line"
                            tone="danger"
                            linkTo="/task-management"
                            linkLabel="View all tasks"
                            note={openTasks != null && openTasks > 0
                                ? `${openTasks} unstarted`
                                : null}
                            noteTone="warning"
                        />
                    </div>


                {statsError ? (
                    <div className="alert alert-warning d-flex align-items-center mb-3 py-2 px-3 small">
                        <i className="ri-error-warning-line me-2"></i>
                        <div className="flex-grow-1">
                            Some live counts couldn't be loaded — the figures above may not be current.
                        </div>
                        <Button size="sm" color="warning" outline onClick={loadStats}>Retry</Button>
                    </div>
                ) : null}

                {/* ════════════════════════════════════════════════════
                    ANNOUNCEMENTS — Round 9 (2026).
                    Single feed driven by /api/announcements. HR/IT-Admin/
                    SuperUser can post, edit, delete (with image upload).
                    Everyone else reads. Replaces the previous static
                    INFO ROW (Announcements/Important Dates/Latest News/
                    Upcoming Events) and SPOTLIGHT ROW (Company Highlights/
                    Employee Spotlight).
                ════════════════════════════════════════════════════ */}
                    <div key="announcements" className="ssw-grid-item"><AnnouncementsBoard /></div>

                {/* ════════════════════════════════════════════════════
                    Quick Actions / Departments / Menu Hub —
                    Replaced by the /workflows launcher route, opened
                    by clicking "General SS White" in the sidebar.
                ════════════════════════════════════════════════════ */}

                {/* ════════════════════════════════════════════════════
                    MY SHORTCUTS + RECENTLY VISITED
                ════════════════════════════════════════════════════ */}

                    <div key="shortcuts" className="ssw-grid-item">
                        <SectionCard
                            icon="ri-bookmark-line"
                            title="My Shortcuts"
                            subtitle="Pages you've pinned for quick access."
                            actions={
                                <Button color="primary" size="sm" onClick={() => setPickerOpen(true)}>
                                    <i className="ri-add-line align-bottom me-1"></i>Add Shortcut
                                </Button>
                            }
                        >
                            {shortcuts.length === 0 || (!q && visibleShortcuts.length === 0) ? (
                                <EmptyState
                                    icon="ri-bookmark-line"
                                    tone="primary"
                                    title="No shortcuts yet"
                                    message="Pin frequently-used pages here for one-click access."
                                    action={
                                        <Button color="primary" onClick={() => setPickerOpen(true)}>
                                            <i className="ri-add-line align-bottom me-1"></i>Add your first shortcut
                                        </Button>
                                    }
                                />
                            ) : visibleShortcuts.length === 0 ? (
                                <EmptyState
                                    icon="ri-search-line"
                                    tone="secondary"
                                    title="No shortcuts match your search"
                                    compact
                                />
                            ) : (
                                <Row className="g-3">
                                    {/* Round PROFILE-1 — widget-style shortcut
                                        cards (same family as the stat row). */}
                                    {visibleShortcuts.map((s) => (
                                        <Col xxl={3} xl={4} md={6} key={s.id ?? s.shortcutKey}>
                                            <ShortcutStatCard
                                                icon={s.icon}
                                                label={s.label}
                                                tone={s.tone}
                                                to={s.path}
                                                onOpen={() =>
                                                    recordVisit({ to: s.path, label: s.label, icon: s.icon })
                                                }
                                                onRemove={() => removeShortcut(s.shortcutKey)}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </SectionCard>
                    </div>

                    <div key="recents" className="ssw-grid-item">
                        <SectionCard
                            icon="ri-history-line"
                            title="Recently Visited"
                            subtitle="Your last 5 clicks from here."
                            actions={
                                recents.length > 0 ? (
                                    <Button color="light" size="sm" onClick={clearRecents}>Clear</Button>
                                ) : null
                            }
                            bodyClassName="p-0"
                        >
                            {recents.length === 0 ? (
                                <EmptyState
                                    icon="ri-history-line"
                                    tone="secondary"
                                    title="Nothing yet"
                                    message="Pages you open from this dashboard will appear here."
                                    compact
                                />
                            ) : visibleRecents.length === 0 ? (
                                <EmptyState icon="ri-search-line" tone="secondary" title="No matches" compact />
                            ) : (
                                <ListGroup flush>
                                    {visibleRecents.map((r) => (
                                        <ListGroupItem
                                            key={r.to}
                                            tag={Link}
                                            to={r.to}
                                            action
                                            className="d-flex align-items-center gap-2 px-3 py-2"
                                            onClick={() =>
                                                recordVisit({ to: r.to, label: r.label, icon: r.icon })
                                            }
                                        >
                                            <span
                                                className="d-inline-flex align-items-center justify-content-center text-primary"
                                                style={{
                                                    width: 30, height: 30, borderRadius: 8,
                                                    background: "var(--ssw-surface-3, #eef1fa)", flexShrink: 0,
                                                }}
                                                aria-hidden="true"
                                            >
                                                <i className={r.icon || "ri-arrow-right-line"} style={{ fontSize: 14 }} />
                                            </span>
                                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                <div className="fw-medium text-truncate" style={{ fontSize: 13 }}>
                                                    {r.label}
                                                </div>
                                                <div className="text-muted" style={{ fontSize: 11 }}>
                                                    {formatRelative(r.ts)}
                                                </div>
                                            </div>
                                            <i className="ri-arrow-right-s-line text-muted" aria-hidden="true"></i>
                                        </ListGroupItem>
                                    ))}
                                </ListGroup>
                            )}
                        </SectionCard>

                        {recentDocs.length > 0 ? (
                            <SectionCard
                                icon="ri-file-text-line"
                                title="Recent Documents"
                                className="mt-3"
                                bodyClassName="p-0"
                                actions={<Badge color="info" pill>{recentDocs.length}</Badge>}
                            >
                                <ListGroup flush>
                                    {recentDocs.map((d) => (
                                        <ListGroupItem
                                            key={d.id}
                                            tag={Link}
                                            to="/document-management"
                                            action
                                            className="d-flex align-items-center gap-2 px-3 py-2"
                                            title={`Open Document Management to find ${d.id}`}
                                        >
                                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                <div className="fw-medium text-truncate" style={{ fontSize: 13 }}>
                                                    <code>{d.id}</code> {d.title}
                                                </div>
                                                <div className="text-muted" style={{ fontSize: 11 }}>
                                                    {d.type || "Document"} · {formatRelative(d.ts)}
                                                </div>
                                            </div>
                                            <i className="ri-arrow-right-s-line text-muted" aria-hidden="true"></i>
                                        </ListGroupItem>
                                    ))}
                                </ListGroup>
                            </SectionCard>
                        ) : null}
                    </div>
                </ResponsiveGridLayout>

                {/* ════════════════════════════════════════════════════
                    (Departments section removed in Round 15 — its
                    contents are now exposed via the MenuHub above.)
                ════════════════════════════════════════════════════ */}

                {/* ════════════════════════════════════════════════════
                    ADD-SHORTCUT PICKER
                ════════════════════════════════════════════════════ */}
                <Modal
                    isOpen={pickerOpen}
                    toggle={() => setPickerOpen(false)}
                    size="lg"
                    centered
                    scrollable
                >
                    <ModalHeader toggle={() => setPickerOpen(false)}>
                        <i className="ri-bookmark-line align-bottom me-1"></i>Add a shortcut
                    </ModalHeader>
                    <ModalBody>
                        {pickerCatalog.length === 0 ? (
                            <EmptyState
                                icon="ri-check-line"
                                tone="success"
                                title="You've pinned everything!"
                                message="All catalog pages are already in your shortcuts."
                                compact
                            />
                        ) : (
                            <Row className="g-3">
                                {pickerCatalog.map((c) => (
                                    <Col xxl={4} md={6} key={c.key}>
                                        <ModuleShortcutCard
                                            icon={c.icon}
                                            label={c.label}
                                            tone={c.tone}
                                            onClick={() => {
                                                addShortcut(c);
                                                setPickerOpen(false);
                                            }}
                                        />
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </ModalBody>
                </Modal>

            </Container>
        </div>
    );
};

export { recordDocumentVisit };
export default Home;
